import React, { useEffect, useState } from 'react';
import moment from 'moment';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useTheme } from '@material-ui/core/styles';

import { getFormat, twoDigits, makeGraph } from 'lib/commonLib';
import { dailyCertCategoryGraph } from 'lib/api/time';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import ContentsWrap from 'components/molecules/ContentsWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';
import SwitchingArea from 'components/organisms/SwitchingArea';

const graphList = [
  {
    title: i18next.t('지출금액'),
    data: { bar: ['ADVRTS_AMT'] },
    yAxisData: {
      left: { ADVRTS_AMT: true },
      right: {},
    },
    referenceLine: {
      ADVRTS_AMT: {
        type: 'average',
        label: i18next.t('지출금액'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('노출'),
    data: { bar: ['PAR_EPRS_CNT'] },
    yAxisData: {
      left: { PAR_EPRS_CNT: true },
      right: {},
    },
    referenceLine: {
      PAR_EPRS_CNT: {
        type: 'average',
        label: i18next.t('노출'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: 'CTR',
    data: { bar: ['CLICK_RATE'] },
    yAxisData: {
      left: { CLICK_RATE: true },
      right: {},
    },
    referenceLine: {
      CLICK_RATE: {
        type: 'average',
        label: 'CTR',
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('세션ROAS'),
    data: { bar: ['SESSION_ROAS'] },
    yAxisData: {
      left: { SESSION_ROAS: true },
      right: {},
    },
    referenceLine: {
      SESSION_ROAS: {
        type: 'average',
        label: i18next.t('세션ROAS'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('쇼핑ROAS'),
    data: { bar: ['TRGT_ROAS'] },
    yAxisData: {
      left: { TRGT_ROAS: true },
      right: {},
    },
    referenceLine: {
      TRGT_ROAS: {
        type: 'average',
        label: i18next.t('쇼핑ROAS'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('직접ROAS'),
    data: { bar: ['DIRECT_ROAS'] },
    yAxisData: {
      left: { DIRECT_ROAS: true },
      right: {},
    },
    referenceLine: {
      DIRECT_ROAS: {
        type: 'average',
        label: i18next.t('직접ROAS'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('총ROAS'),
    data: { bar: ['ROAS'] },
    yAxisData: {
      left: { ROAS: true },
      right: {},
    },
    referenceLine: {
      ROAS: {
        type: 'average',
        label: i18next.t('총ROAS'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('클릭수'),
    data: { bar: ['CLICK_CNT'] },
    yAxisData: {
      left: { CLICK_CNT: true },
      right: {},
    },
    referenceLine: {
      CLICK_CNT: {
        type: 'average',
        label: i18next.t('클릭수'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
  {
    title: i18next.t('전환수'),
    data: { bar: ['ORDER_CNT'] },
    yAxisData: {
      left: { ORDER_CNT: true },
      right: {},
    },
    referenceLine: {
      ORDER_CNT: {
        type: 'average',
        label: i18next.t('전환수'),
        visibleValue: true,
        color: '#f66172',
      },
    },
  },
];

const TopCategoryStatisticsGraph = ({ params }, location) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { locale } = useSelector(({ locale }) => ({
    locale: locale.locale,
  }));

  const [infoMessage, setInfoMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(false);
  const [graphData, setGraphData] = useState(false);
  const [curGraphComponent, setCurGraphComponent] = useState(false);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps,
  }, []);

  useEffect(() => {
    handleChangeGraph(graphList[0], 0)();
    // eslint-disable-next-line react-hooks/exhaustive-deps,
  }, [graphData]);

  const getData = () => {
    const { sDate, eDate } = params;
    const format = getFormat(locale);

    setInfoMessage(
      t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      })
    );

    /*
      1 : 종합대형쇼핑몰
      2 : 여성패션
      3 : 남성패션
      4 : 여행 (여행사, 항공(면세점 제외))
      5 : 화장품
     */
    dailyCertCategoryGraph({
      ...params,
      uri: '/report/daily/topCategory',
    })
      .then((response) => {
        const { data } = response.data;
        setGraphData(data);
        setLoading(false);
        setError(false);
      })
      .catch((e) => {
        setLoading(false);
        setError(true);
      });
  };

  /**
   * 새로고침
   */
  const handleRefesh = () => {
    getData();
  };

  const xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const dateObj = moment(value, 'YYYYMMDD');
    const day = dateObj.day();

    const dayStr = makeLabel(true)(value);
    let fill = '';
    //Sunday as 0 and Saturday as 6.
    if (day === 0) {
      fill = theme.palette.common.red;
    } else if (day === 6) {
      fill = theme.palette.common.blue;
    }

    return (
      <text x={x} y={y + 10} fill={fill} textAnchor="middle">
        {dayStr}
      </text>
    );
  };

  // 월 일 or 일 반환
  const makeLabel = (onlyDay) => (originalDate) => {
    const dateObj = moment(originalDate, 'YYYYMMDD');
    const month = twoDigits(dateObj.month() + 1);
    const date = dateObj.date();
    const day = dateObj.day();
    let color = '';

    if (day === 0) {
      color = theme.palette.common.red;
    } else if (day === 6) {
      color = theme.palette.common.blue;
    }

    return onlyDay ? (
      `${twoDigits(date)}${t('일')}`
    ) : (
      <span style={{ color }}>{`${t(month + '월')} ${twoDigits(date)}${t(
        '일'
      )}`}</span>
    );
  };

  const handleChangeGraph =
    (
      {
        title,
        data,
        yAxisData,
        customizeData: customize,
        isPercent = false,
        referenceLine,
      },
      index
    ) =>
    (e) => {
      if (typeof data !== 'undefined') {
        let graphComponent = [];

        if (graphData !== false) {
          graphComponent.push(
            graphData.map((item, key) => {
              let primitiveData = item.data;
              const {
                graphData,
                dataKeys,
                customizeData,
                keyLabel,
                stackId,
                referenceData,
              } = makeGraph({ data, customize, primitiveData, referenceLine });

              const result = (
                <MultiplexChart
                  width="100%"
                  xAxisTick={xAxisTick}
                  makeLabel={makeLabel(false)}
                  data={graphData}
                  dataKeys={dataKeys}
                  customizeData={customizeData}
                  keyLabel={keyLabel}
                  stackId={stackId.length !== 0 ? stackId.join('$') : null}
                  yAxisData={yAxisData}
                  legend
                  title={item.info.CTGR_NM}
                  isPercent={isPercent}
                  useTitle={true}
                  key={title + key}
                  referenceLine={referenceData}
                />
              );
              return result;
            })
          );
        }

        setSelectedIdx(index);
        setCurGraphComponent(graphComponent);
      }
    };

  return (
    <SwitchingArea
      isOpen={false}
      title={
        <>
          <InfoTitleArea
            title={t('주요카테고리별 그래프')}
            tooltipTitle={t(infoMessage)}
            iconType="fainfoCircle"
            iconColor="graph"
          />
        </>
      }
      handleRefesh={handleRefesh}
    >
      {loading || error ? (
        <CurrentStatus type={loading ? 'loading' : error && 'error'} />
      ) : (
        <>
          <ChartButtonWrap>
            {graphList.map((item, index) => {
              return (
                <OutlinedButton
                  key={item.title}
                  onClick={handleChangeGraph(item, index)}
                  selected={selectedIdx === index}
                >
                  {item.title}
                </OutlinedButton>
              );
            })}
          </ChartButtonWrap>
          <ContentsWrap contentsCount={1}>{curGraphComponent}</ContentsWrap>
        </>
      )}
    </SwitchingArea>
  );
};

export default TopCategoryStatisticsGraph;
